import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../environments/environment';
import { ApiResponse } from '../models/api-response';
import {
  BillingInterval, BillingSummary, CheckoutResponse, PlanDto, TenantSubscriptionRow,
} from '../models/billing';
import { TenantFeaturesService } from './tenant-features.service';

@Injectable({ providedIn: 'root' })
export class BillingService {
  private baseUrl = `${environment.apiUrl}/Billing`;
  private adminUrl = `${environment.apiUrl}/BillingAdmin`;

  /** Latest summary, shared with the layout banner + entitlement-based menu hiding. */
  private summary$ = new BehaviorSubject<BillingSummary | null>(null);
  readonly summary = this.summary$.asObservable();

  constructor(private http: HttpClient, private tenantFeatures: TenantFeaturesService) {}

  loadSummary(): Observable<ApiResponse<BillingSummary>> {
    return this.http.get<ApiResponse<BillingSummary>>(`${this.baseUrl}/Summary`).pipe(
      tap((res) => { if (res.isSuccess) this.summary$.next(res.value); }),
    );
  }

  get current(): BillingSummary | null { return this.summary$.value; }

  /**
   * Entitlement check. With a loaded billing summary (Administrator / TenantAdmin) it reads the plan
   * (true when the tenant has no subscription / is ungated). Without one — every other role, whose
   * summary is never loaded — it delegates to TenantFeaturesService (GET Tenants/MyFeatures), which
   * is false only when the server explicitly said so.
   */
  isEnabled(key: string): boolean {
    const summary = this.summary$.value;
    if (!summary) return this.tenantFeatures.isEntitled(key);
    const ent = summary.entitlements;
    if (!ent || Object.keys(ent).length === 0) return true;
    return ent[key] === 'true';
  }

  /** Forget the cached summary (logout / user switch). */
  clear(): void {
    this.summary$.next(null);
  }

  plans(): Observable<ApiResponse<PlanDto[]>> {
    return this.http.get<ApiResponse<PlanDto[]>>(`${this.baseUrl}/Plans`);
  }
  /** Anonymous pricing for the public portal / marketing page. */
  publicPlans(): Observable<ApiResponse<PlanDto[]>> {
    return this.http.get<ApiResponse<PlanDto[]>>(`${this.baseUrl}/PublicPlans`);
  }
  changePlan(planCode: string, interval: BillingInterval): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.baseUrl}/ChangePlan`, { planCode, interval });
  }
  checkout(): Observable<ApiResponse<CheckoutResponse>> {
    return this.http.post<ApiResponse<CheckoutResponse>>(`${this.baseUrl}/Checkout`, {});
  }
  cancel(): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.baseUrl}/Cancel`, {});
  }

  // ── super-admin ────────────────────────────────────────────────────────────
  subscriptions(): Observable<ApiResponse<TenantSubscriptionRow[]>> {
    return this.http.get<ApiResponse<TenantSubscriptionRow[]>>(`${this.adminUrl}/Subscriptions`);
  }
  markPaid(invoiceId: string, note?: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.adminUrl}/MarkPaid`, { invoiceId, note });
  }
}
